import type { MutationTransaction } from "./storeContracts";

export interface CreateMutationTransactionInput<TSnapshot, TPayload> {
  id: string;
  action: string;
  payload: TPayload;
  rollbackSnapshot: TSnapshot;
}

export interface MutationTransactionFailure {
  message: string;
}

export function createPendingTransaction<TSnapshot, TPayload>(
  input: CreateMutationTransactionInput<TSnapshot, TPayload>,
): MutationTransaction<TSnapshot, TPayload> {
  return {
    id: input.id,
    action: input.action,
    payload: input.payload,
    rollbackSnapshot: input.rollbackSnapshot,
    status: "pending",
  };
}

export function markTransactionCommitted<TSnapshot, TPayload>(
  transaction: MutationTransaction<TSnapshot, TPayload>,
): MutationTransaction<TSnapshot, TPayload> {
  return {
    ...transaction,
    status: "committed",
    errorMessage: undefined,
  };
}

export function markTransactionRolledBack<TSnapshot, TPayload>(
  transaction: MutationTransaction<TSnapshot, TPayload>,
): MutationTransaction<TSnapshot, TPayload> {
  return {
    ...transaction,
    status: "rolledBack",
  };
}

export function markTransactionFailed<TSnapshot, TPayload>(
  transaction: MutationTransaction<TSnapshot, TPayload>,
  failure: MutationTransactionFailure,
): MutationTransaction<TSnapshot, TPayload> {
  return {
    ...transaction,
    status: "failed",
    errorMessage: failure.message,
  };
}

export function isTerminalTransactionStatus(
  status: MutationTransaction<unknown, unknown>["status"],
): boolean {
  return status === "committed" || status === "rolledBack" || status === "failed";
}

export function isPendingTransaction<TSnapshot, TPayload>(
  transaction: MutationTransaction<TSnapshot, TPayload>,
): boolean {
  return transaction.status === "pending";
}

export function getRollbackSnapshot<TSnapshot, TPayload>(
  transaction: MutationTransaction<TSnapshot, TPayload>,
): TSnapshot {
  return transaction.rollbackSnapshot;
}

